import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LogIn, AlertCircle, UserPlus } from "lucide-react";
import { login, loginWithGoogle } from "@/services/authService";
import { SEO } from "@/components/ui/seo";
import { Separator } from "@/components/ui/separator";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [redirectPath, setRedirectPath] = useState("/");

  // Lấy tham số redirect từ URL khi component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const redirect = searchParams.get("redirect");
    if (redirect) {
      setRedirectPath(redirect);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Gọi service đăng nhập
      const response = await login({ email, password });

      // Lưu thông tin session vào localStorage
      if (response.session) {
        localStorage.setItem("token", response.session.access_token);
        localStorage.setItem("refreshToken", response.session.refresh_token);
        localStorage.setItem("user", JSON.stringify(response.user));
      }

      // Chuyển hướng về trang được chỉ định hoặc trang chủ
      navigate(redirectPath);
    } catch (err: any) {
      setError(err.message || "Đăng nhập thất bại");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Xác định URL chuyển hướng sau khi đăng nhập thành công
      const redirectUrl = redirectPath 
        ? `${window.location.origin}${redirectPath}` 
        : window.location.origin;
      
      // Gọi hàm đăng nhập với Google
      await loginWithGoogle(redirectUrl);
      // Không cần chuyển hướng ở đây vì Supabase sẽ xử lý
    } catch (err: any) {
      setError(err.message || "Đăng nhập với Google thất bại");
    }
  };

  return (
    <div className="container flex items-center justify-center min-h-[calc(100vh-4rem)] py-8">
      <SEO 
        title="Đăng nhập | Giác Ngộ"
        description="Đăng nhập vào tài khoản Giác Ngộ để truy cập các khóa học, sơ đồ và tài liệu Phật giáo độc quyền"
        ogType="website"
      />
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {t("login", { ns: "auth" })}
          </CardTitle>
          <CardDescription className="text-center">
            {t("loginDescription", { ns: "auth" })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("email", { ns: "auth" })}</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">
                  {t("password", { ns: "auth" })}
                </Label>
                <Button
                  variant="link"
                  className="p-0 h-auto text-sm"
                  onClick={() => navigate("/forgot-password")}
                >
                  {t("forgotPassword", { ns: "auth" })}
                </Button>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {t("common.loading")}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  {t("login", { ns: "auth" })}
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {t("orContinueWith", { ns: "auth", defaultValue: "Hoặc tiếp tục với" })}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleGoogleLogin}
                type="button"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                  <path d="M1 1h22v22H1z" fill="none" />
                </svg>
                {t("loginWithGoogle", { ns: "auth", defaultValue: "Đăng nhập với Google" })}
              </Button>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="text-center text-sm">
            {t("noAccount", { ns: "auth" })}{" "}
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate("/register")}
          >
            <span className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              {t("register", { ns: "auth" })}
            </span>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
